/*
 * Conversor de moedas automático (visual) por geolocalização.
 *
 * - Detecta o país do visitante por IP (ipwho.is) uma única vez e guarda
 *   no localStorage para não refazer a requisição a cada troca de página.
 * - Aplica a moeda do país pelo mapa configurável; país não mapeado -> fallback (USD).
 * - Conversão é PURAMENTE VISUAL (não toca no checkout). Reaproveita o motor
 *   `Currency` (jquery.currencies.min.js + taxas da CDN da Shopify).
 * - Converte a partir do valor BRUTO em centavos (data-mdp-cents), evitando o
 *   problema de `money_without_trailing_zeros` na leitura do texto exibido.
 *
 * Configuração injetada em window.MDP_CURRENCY pelo snippet currency-auto-visual.liquid.
 */
(function () {
  'use strict';

  var cfg = window.MDP_CURRENCY || {};
  var SHOP_CURRENCY = (cfg.shopCurrency || 'BRL').toUpperCase();
  var FALLBACK = (cfg.fallback || 'USD').toUpperCase();
  var CACHE_DAYS = parseInt(cfg.cacheDays, 10) || 7;
  var STORAGE_KEY = 'mdp_currency_geo';

  // Provedor de geolocalização por IP: geojs.io (gratuito, HTTPS, CORS, sem chave
  // e sem limite rígido publicado). Como o resultado é cacheado no localStorage, o
  // volume de requisições é ~1 por visitante novo a cada TTL (não por pageview).
  // Pode ser sobrescrito via window.MDP_CURRENCY.geoProviders.
  var DEFAULT_GEO_PROVIDERS = [
    { url: 'https://get.geojs.io/v1/ip/country.json', fields: ['country', 'country_code'] }
  ];
  var GEO_PROVIDERS = (cfg.geoProviders && cfg.geoProviders.length) ? cfg.geoProviders : DEFAULT_GEO_PROVIDERS;

  // ---- Mapa país -> moeda (parse das linhas "CC:CUR") ----------------------
  function parseMap(raw) {
    var map = {};
    if (!raw) return map;
    String(raw).split(/\r?\n/).forEach(function (line) {
      line = line.trim();
      if (!line || line.charAt(0) === '#') return;
      var parts = line.split(':');
      if (parts.length < 2) return;
      var country = parts[0].trim().toUpperCase();
      var currency = parts[1].trim().toUpperCase();
      if (country && currency) map[country] = currency;
    });
    return map;
  }

  var COUNTRY_MAP = parseMap(cfg.mapRaw);

  // ---- Cache de localização (localStorage) ---------------------------------
  function readCache() {
    try {
      var raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      var data = JSON.parse(raw);
      if (!data || !data.currency || !data.ts) return null;
      var ageMs = Date.now() - data.ts;
      if (ageMs > CACHE_DAYS * 24 * 60 * 60 * 1000) return null;
      return data;
    } catch (e) {
      return null;
    }
  }

  function writeCache(currency, country) {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ currency: currency, country: country || '', ts: Date.now() })
      );
    } catch (e) {
      /* localStorage indisponível: segue sem cache */
    }
  }

  function currencyForCountry(countryCode) {
    var cc = (countryCode || '').toUpperCase();
    return COUNTRY_MAP[cc] || FALLBACK;
  }

  function pickCountry(data, fields) {
    if (!data) return null;
    for (var i = 0; i < fields.length; i++) {
      var v = data[fields[i]];
      if (v && String(v).length === 2) return String(v).toUpperCase();
    }
    return null;
  }

  // Tenta os provedores em ordem; resolve com o código de país (ou null).
  function fetchCountry(index) {
    if (index >= GEO_PROVIDERS.length) return Promise.resolve(null);
    var p = GEO_PROVIDERS[index];
    return fetch(p.url)
      .then(function (res) {
        return res.json();
      })
      .then(function (data) {
        var country = pickCountry(data, p.fields || ['country_code']);
        return country || fetchCountry(index + 1);
      })
      .catch(function () {
        return fetchCountry(index + 1);
      });
  }

  // Resolve a moeda alvo. Retorna uma Promise<string>.
  function resolveCurrency() {
    var cached = readCache();
    if (cached) return Promise.resolve(cached.currency);

    if (!window.fetch) return Promise.resolve(FALLBACK);

    return fetchCountry(0).then(function (country) {
      if (!country) {
        // Nenhum provedor respondeu: usa fallback e NÃO cacheia (tenta de novo depois).
        return FALLBACK;
      }
      var currency = currencyForCountry(country);
      writeCache(currency, country);
      return currency;
    });
  }

  // ---- Garante que o motor Currency + taxas estejam carregados -------------
  function waitForRates() {
    return new Promise(function (resolve, reject) {
      var tries = 0;
      (function check() {
        if (window.Currency && Currency.rates && Currency.convert) {
          resolve();
          return;
        }
        if (tries++ > 100) {
          reject(new Error('Currency rates não carregaram'));
          return;
        }
        setTimeout(check, 50);
      })();
    });
  }

  // ---- Conversão dos preços marcados ---------------------------------------
  // Convertemos sempre a partir do valor BRUTO em centavos (data-mdp-cents),
  // que é imutável e está sempre na moeda da loja. Isso evita o problema de
  // `money_without_trailing_zeros` (leitura de texto) e a armadilha de cache do
  // Currency.convertAll. A idempotência é por comparação `innerHTML !== html`:
  // auto-corretiva (reconverte quando o tema sobrescreve com BRL) e sem auto-loop
  // do observer (quando já está correto, não reescreve).
  var noRateWarned = false;

  function convertElement(el, target) {
    if (!el) return;

    var cents = parseInt(el.getAttribute('data-mdp-cents'), 10);
    if (isNaN(cents)) return;

    if (!Currency.rates[target] || !Currency.rates[SHOP_CURRENCY]) {
      // Moeda não habilitada na Shopify (sem taxa de câmbio). Mantém a moeda da loja.
      if (!noRateWarned) {
        noRateWarned = true;
        console.warn('[mdp-currency] sem taxa de câmbio para', target, '— exibindo moeda da loja.');
      }
      return;
    }

    var converted = Currency.convert(cents, SHOP_CURRENCY, target);
    var format =
      (Currency.moneyFormats[target] && Currency.moneyFormats[target].money_format) ||
      '{{amount}}';
    var html = Currency.formatMoney(converted, format);

    // Idempotência confiável: o browser re-serializa o innerHTML (decodifica
    // entidades como &euro;/&nbsp;), então comparar contra a string crua `html`
    // faz a guarda falhar SEMPRE e o MutationObserver entra em loop infinito.
    // Em vez disso, comparamos contra a última saída NOSSA já normalizada.
    if (el.__mdpRendered != null && el.innerHTML === el.__mdpRendered) return;

    el.innerHTML = html;
    el.__mdpRendered = el.innerHTML; // valor normalizado pelo browser (ponto fixo)
  }

  function convertPage(target) {
    var nodes = document.querySelectorAll('.mdp-money[data-mdp-cents]');
    for (var i = 0; i < nodes.length; i++) {
      convertElement(nodes[i], target);
    }
  }

  // ---- Re-conversão em conteúdo dinâmico (SÍNCRONA) ------------------------
  // O callback do MutationObserver roda como microtask, ANTES do paint. Ao
  // converter os nós afetados imediatamente (sem debounce/setTimeout), o BRL
  // que o tema reescreve e a nossa reconversão acontecem no mesmo frame — então
  // o preço original nunca chega a ser pintado (sem flash).
  function convertWithin(node, target) {
    if (!node || node.nodeType !== 1) return; // só elementos
    if (node.matches && node.matches('.mdp-money[data-mdp-cents]')) {
      convertElement(node, target);
    }
    if (node.querySelectorAll) {
      var inner = node.querySelectorAll('.mdp-money[data-mdp-cents]');
      for (var i = 0; i < inner.length; i++) convertElement(inner[i], target);
    }
  }

  function closestMoney(node) {
    var el = node && node.nodeType === 1 ? node : node && node.parentElement;
    if (el && el.closest) return el.closest('.mdp-money[data-mdp-cents]');
    return null;
  }

  function watchDynamic(target) {
    if (!window.MutationObserver) return;

    var observer = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var m = mutations[i];

        // Nós recém-inseridos (section rendering, drawer do carrinho, etc.).
        for (var j = 0; j < m.addedNodes.length; j++) {
          convertWithin(m.addedNodes[j], target);
        }

        // Conteúdo reescrito dentro de um .mdp-money existente (ex.: `.text()`).
        var money = closestMoney(m.target);
        if (money) convertElement(money, target);
      }
    });

    observer.observe(document.body, { childList: true, subtree: true, characterData: true });
  }

  // ---- Bootstrap -----------------------------------------------------------
  function reveal() {
    document.documentElement.classList.remove('mdp-prices-hidden');
  }

  function start() {
    resolveCurrency().then(function (target) {
      target = (target || FALLBACK).toUpperCase();

      // Visitante na moeda da loja (ex.: Brasil/BRL): nada a converter.
      if (target === SHOP_CURRENCY) {
        reveal();
        return;
      }

      waitForRates()
        .then(function () {
          convertPage(target);
          watchDynamic(target);
          reveal();
        })
        .catch(function (err) {
          console.warn('[mdp-currency]', err.message);
          reveal();
        });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
